import { type ReactNode } from 'react';
import { useNavSections } from './useNavSections';
import { ShellProvider, useShell } from './ShellProvider';
import { AnnouncementBanner } from '../views/AnnouncementBanner';
import { SKINS } from './skins';
import { NovaAssistant } from '../nova/NovaAssistant';
import { ActivityBar } from './ActivityBar';
import type { ShellSkin } from './types';

// Shell: the routed layout element. It merges every module's nav once, groups it into areas,
// then renders whichever skin the viewer has chosen. Swapping the frame never touches the
// screens — each skin renders react-router's <Outlet/> for page content.

export interface ShellRootProps {
  brand?: ReactNode;
  brandShort?: ReactNode;
  footer?: ReactNode;
  badges?: Record<string, number | string>;
  /** Override the registered skins (tests). */
  skins?: ShellSkin[];
  /** Force a skin regardless of the stored preference (tests / previews). */
  skin?: string;
}

function ShellInner({ brand = 'NovaPSA', brandShort = 'Nova', footer, badges }: ShellRootProps) {
  const { skin, skins } = useShell();
  const { sections, areas } = useNavSections();
  const active = skins.find((s) => s.id === skin) ?? skins[0];
  if (!active) return null;
  const Skin = active.Component;
  // The feedback button lives in each skin's top-bar (a "?" icon next to the theme toggle), so it
  // never collides with the Nova assistant that floats bottom-right. Only the assistant is rendered
  // here, above whichever frame the viewer chose.
  return (
    <>
      <ActivityBar />
      <AnnouncementBanner />
      <Skin brand={brand} brandShort={brandShort} footer={footer} badges={badges} sections={sections} areas={areas} />
      <NovaAssistant />
    </>
  );
}

export function Shell({ skins = SKINS, skin, ...rest }: ShellRootProps) {
  return (
    <ShellProvider skins={skins} initialSkin={skin}>
      <ShellInner {...rest} />
    </ShellProvider>
  );
}
